// src/lib/journeyService.ts
//
// BekalNanti.ID — Perencanaan Data Service
// Interacts with canonical schema: bekalnanti_app

import { supabase } from './supabase'
import { User } from '@supabase/supabase-js'
import {
  JourneyData,
  GoalRecord,
  NextActionItem,
  MissingInfoItem,
  PlanningStage,
  StageStatus,
} from '../journey/types'
import { fetchFinancialReality } from './financeService'
import { formatCompactIDR } from './financialCalculation'
import { fetchHouseholdGoalTargets } from './targetService'
import { evaluateTargetReadiness } from './targetSpecification'
import {
  fetchHouseholdGoalCostItems,
  fetchHouseholdGoalFundingSources,
} from './costFundingService'
import { evaluateP4Readiness } from './costFunding'
import { getGoalDomain } from './goalDomainRegistry'
import { reconcileLegacyHouseholdName } from './peopleService'

/**
 * Fetch the complete planning journey state for an authenticated user.
 * Returns null if user has not yet initialized a profile or household.
 */
export async function fetchJourneyData(user: User): Promise<JourneyData | null> {
  // 1. Fetch Profile
  const { data: profile, error: profErr } = await supabase
    .from('profiles')
    .select('id, display_name, preferred_language, default_currency')
    .eq('auth_user_id', user.id)
    .maybeSingle()

  if (profErr) {
    throw new Error(`Gagal memuat profil: ${profErr.message}`)
  }
  if (!profile) return null

  // 2. Fetch Household
  const { data: household, error: houseErr } = await supabase
    .from('households')
    .select('id, name')
    .eq('created_by_profile_id', profile.id)
    .maybeSingle()

  if (houseErr) {
    throw new Error(`Gagal memuat rumah tangga: ${houseErr.message}`)
  }
  if (!household) return null

  // One-time reconciliation for legacy generated names (e.g. "Keluarga Burner Mail")
  const effectiveHouseholdName = await reconcileLegacyHouseholdName(
    household.id,
    household.name,
    profile.display_name
  )

  // 3. Fetch Active Persons (canonical active planning set)
  const { data: persons, error: persErr } = await supabase
    .from('persons')
    .select('id, full_name, relationship, status, date_of_birth, created_at, updated_at')
    .eq('household_id', household.id)
    .eq('status', 'active')
    .order('created_at', { ascending: true })

  if (persErr) {
    throw new Error(`Gagal memuat data keluarga: ${persErr.message}`)
  }

  // 3b. Fetch Archived Persons (for dedicated archive/history views)
  const { data: archivedPersonsData } = await supabase
    .from('persons')
    .select('id, full_name, relationship, status, date_of_birth, created_at, updated_at')
    .eq('household_id', household.id)
    .eq('status', 'archived')
    .order('created_at', { ascending: true })

  // 4. Fetch Goals (active only)
  const { data: goals, error: goalsErr } = await supabase
    .from('goals')
    .select('id, household_id, person_id, goal_type, name, status, priority, created_at, updated_at')
    .eq('household_id', household.id)
    .neq('status', 'archived')
    .order('created_at', { ascending: true })

  if (goalsErr) {
    throw new Error(`Gagal memuat tujuan bekal: ${goalsErr.message}`)
  }

  // 5. Fetch Full Financial Reality (P2)
  const financialReality = await fetchFinancialReality(household.id)

  // 6. Fetch Target Specifications (P3)
  const rawGoalIds = (goals || []).map((g: any) => g.id)
  const targetsMap = await fetchHouseholdGoalTargets(rawGoalIds)

  // 7. Fetch Cost Items & Funding Sources (P4)
  const [costItemsMap, fundingSourcesMap] = await Promise.all([
    fetchHouseholdGoalCostItems(rawGoalIds),
    fetchHouseholdGoalFundingSources(rawGoalIds),
  ])

  const formattedGoals: GoalRecord[] = (goals || []).map((g: any) => ({
    id: g.id,
    householdId: g.household_id,
    personId: g.person_id,
    goalType: g.goal_type,
    name: g.name,
    status: g.status || 'draft',
    priority: (g.priority as 'high' | 'medium' | 'low') || 'medium',
    createdAt: g.created_at,
    updatedAt: g.updated_at,
    target: targetsMap[g.id] || null,
    costItems: costItemsMap[g.id] || [],
    fundingSources: fundingSourcesMap[g.id] || [],
  }))

  const hasIncomes = financialReality.incomes.length > 0
  const hasExpenses = financialReality.expenses.length > 0
  const hasSavings = financialReality.savings.length > 0
  const hasDebts = financialReality.debts.length > 0
  const hasObligations = financialReality.obligations.length > 0

  return {
    profile: {
      id: profile.id,
      displayName: profile.display_name,
      preferredLanguage: profile.preferred_language,
      defaultCurrency: profile.default_currency,
    },
    household: {
      id: household.id,
      name: effectiveHouseholdName,
    },
    persons: (persons || []).map((p: any) => ({
      id: p.id,
      fullName: p.full_name,
      relationship: p.relationship,
      status: (p.status as 'active' | 'archived') || 'active',
      dateOfBirth: p.date_of_birth || null,
      createdAt: p.created_at,
      updatedAt: p.updated_at,
    })),
    archivedPersons: (archivedPersonsData || []).map((p: any) => ({
      id: p.id,
      fullName: p.full_name,
      relationship: p.relationship,
      status: (p.status as 'active' | 'archived') || 'archived',
      dateOfBirth: p.date_of_birth || null,
      createdAt: p.created_at,
      updatedAt: p.updated_at,
    })),
    goals: formattedGoals,
    financialReality,
    financialStatus: {
      hasIncomes,
      hasExpenses,
      hasSavings,
      hasDebts,
      hasObligations,
    },
    targetSpecifications: targetsMap,
  }
}

/**
 * Update an existing goal in bekalnanti_app.goals.
 */
export async function updateGoal(
  goalId: string,
  updates: {
    name?: string
    goal_type?: string
    priority?: 'high' | 'medium' | 'low'
    status?: string
    person_id?: string | null
  }
): Promise<void> {
  const payload: any = {
    ...updates,
    updated_at: new Date().toISOString(),
  }

  const { error } = await supabase
    .from('goals')
    .update(payload)
    .eq('id', goalId)

  if (error) {
    throw new Error(`Gagal memperbarui tujuan: ${error.message}`)
  }
}

/**
 * Add a new goal to the user's household.
 */
export async function addGoal(
  householdId: string,
  goal: {
    name: string
    goal_type: string
    priority?: 'high' | 'medium' | 'low'
    person_id?: string | null
  }
): Promise<GoalRecord> {
  const { data, error } = await supabase
    .from('goals')
    .insert({
      household_id: householdId,
      name: goal.name,
      goal_type: goal.goal_type,
      priority: goal.priority || 'medium',
      status: 'draft',
      person_id: goal.person_id || null,
    })
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal menambahkan tujuan baru: ${error.message}`)
  }

  return {
    id: data.id,
    householdId: data.household_id,
    personId: data.person_id,
    goalType: data.goal_type,
    name: data.name,
    status: data.status,
    priority: data.priority,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

/**
 * Delete a goal from bekalnanti_app.goals.
 */
export async function deleteGoal(goalId: string): Promise<void> {
  const { error } = await supabase.from('goals').delete().eq('id', goalId)

  if (error) {
    throw new Error(`Gagal menghapus tujuan: ${error.message}`)
  }
}

/**
 * Deterministic Planning Stages Generator.
 * Represents real mapped progress:
 * 1. Goal (Selesai if goals > 0)
 * 2. Current Reality (Selesai if incomes & expenses mapped)
 * 3. Planning Data (Sedang dipetakan / Selesai based on scoped goal target)
 * 4. Cost & Funding (Sedang dipetakan / Selesai based on scoped goal costs)
 * 5. Calculation (Siap dijalankan when costs mapped)
 * 6. Reality Check (Siap dievaluasi when calculation ready & financial mapped)
 * 7. PetaEsok (Siap dijelajahi when calculation ready & financial mapped)
 * 8. Next Step (Menunggu data)
 */
export function getPlanningStages(
  journey: JourneyData,
  selectedGoalId?: string | null
): PlanningStage[] {
  const hasGoals = journey.goals.length > 0
  const hasFinancial = journey.financialStatus.hasIncomes && journey.financialStatus.hasExpenses
  const netCapacity = journey.financialReality?.summary.netMonthlyCapacity ?? 0

  if (!hasGoals) {
    return [
      {
        id: 'goal',
        number: 1,
        title: 'Tujuan Masa Depan',
        subtitle: 'Belum ditentukan',
        status: 'in_progress',
        statusLabel: 'Sedang dipetakan',
      },
      {
        id: 'reality',
        number: 2,
        title: 'Kondisi Saat Ini (Realita)',
        subtitle: hasFinancial
          ? `Arus kas terpetakan (${formatCompactIDR(netCapacity)}/bln)`
          : 'Arus kas & kesiapan tabungan',
        status: hasFinancial ? 'completed' : 'not_started',
        statusLabel: hasFinancial ? 'Selesai' : 'Belum dimulai',
      },
      {
        id: 'planning_data',
        number: 3,
        title: 'Spesifikasi Sasaran',
        subtitle: 'Menunggu tujuan pertama dipilih',
        status: 'waiting_data',
        statusLabel: 'Menunggu tujuan',
      },
      {
        id: 'cost_funding',
        number: 4,
        title: 'Estimasi Biaya & Pendanaan',
        subtitle: 'Estimasi biaya aktif setelah sasaran dipilih',
        status: 'waiting_data',
        statusLabel: 'Menunggu data',
      },
      {
        id: 'calculation',
        number: 5,
        title: 'Kalkulasi Deterministik',
        subtitle: 'Kalkulasi aktif setelah biaya dipetakan',
        status: 'waiting_data',
        statusLabel: 'Menunggu data',
      },
      {
        id: 'reality_check',
        number: 6,
        title: 'Reality Check',
        subtitle: 'Analisis gap kapasitas vs kebutuhan aktual',
        status: 'waiting_data',
        statusLabel: 'Menunggu data',
      },
      {
        id: 'peta_esok',
        number: 7,
        title: 'PetaEsok (Peta Masa Depan)',
        subtitle: 'Visualisasi tahapan dan jalur alternatif',
        status: 'waiting_data',
        statusLabel: 'Menunggu data',
      },
      {
        id: 'next_steps',
        number: 8,
        title: 'Langkah Konkret',
        subtitle: 'Tindakan prioritas dan snapshot perencanaan',
        status: 'waiting_data',
        statusLabel: 'Menunggu data',
      },
    ]
  }

  // Scoped goal selection: selectedGoalId, or first active goal
  const activeGoal =
    (selectedGoalId ? journey.goals.find((g) => g.id === selectedGoalId) : null) ||
    journey.goals[0]!
  const domain = getGoalDomain(activeGoal.goalType)
  const target = activeGoal.target
  const targetEval = evaluateTargetReadiness(target, activeGoal.goalType)
  const p4Eval = evaluateP4Readiness(
    activeGoal.costItems || [],
    activeGoal.fundingSources || [],
    activeGoal.goalType
  )

  // Stage 3: Target Specification Readiness
  let stage3Status: StageStatus = 'not_started'
  let stage3Label = 'Belum dimulai'
  let stage3Subtitle = `Spesifikasi sasaran ${domain.title.toLowerCase()}`

  if (targetEval.isComplete) {
    stage3Status = 'completed'
    stage3Label = 'Selesai'
    stage3Subtitle = `${domain.title}: Target ${targetEval.summaryText}`
  } else if (targetEval.completedFieldsCount > 0) {
    stage3Status = 'in_progress'
    stage3Label = 'Sedang dipetakan'
    stage3Subtitle = `${activeGoal.name}: ${targetEval.completedFieldsCount}/4 data kunci terisi`
  } else if (hasFinancial) {
    stage3Status = 'in_progress'
    stage3Label = 'Siap dimulai'
    stage3Subtitle = `${domain.title}: Tentukan ${domain.targetStageLabel.toLowerCase()}, ${domain.programNameLabel.toLowerCase()}, dan lokasi`
  }

  // Stage 4: Cost & Funding Readiness
  let stage4Status: StageStatus = 'waiting_data'
  let stage4Label = 'Menunggu data'
  let stage4Subtitle = `Menunggu spesifikasi sasaran ${domain.title.toLowerCase()}`

  if (p4Eval.isReadyForCalculation) {
    stage4Status = 'completed'
    stage4Label = 'Selesai'
    stage4Subtitle = `${activeGoal.costItems?.length || 0} komponen biaya & modal (${domain.title})`
  } else if (p4Eval.hasCosts) {
    stage4Status = 'in_progress'
    stage4Label = 'Sedang dipetakan'
    stage4Subtitle = `${activeGoal.costItems?.length || 0} komponen modal/biaya tercatat`
  } else if (targetEval.isComplete) {
    stage4Status = 'in_progress'
    stage4Label = 'Siap dimulai'
    stage4Subtitle = `Petakan modal awal, izin usaha, & biaya ${domain.title.toLowerCase()}`
  }

  // Stage 5: Calculation Readiness
  const isCalcReady = p4Eval.isReadyForCalculation
  const stage5Status: StageStatus = isCalcReady ? 'in_progress' : 'waiting_data'
  const stage5Label = isCalcReady ? 'Siap dijalankan' : 'Menunggu data'
  const stage5Subtitle = isCalcReady
    ? `Perhitungan proyeksi kebutuhan modal & bekal ${domain.title.toLowerCase()}`
    : `Kalkulasi aktif setelah modal & biaya ${domain.title.toLowerCase()} dipetakan`

  // Stage 6: Reality Check Readiness
  const isRealityCheckReady = isCalcReady && hasFinancial
  const stage6Status: StageStatus = isRealityCheckReady ? 'in_progress' : 'waiting_data'
  const stage6Label = isRealityCheckReady ? 'Siap dievaluasi' : 'Menunggu data'
  const stage6Subtitle = isRealityCheckReady
    ? `Uji kesiapan arus kas keluarga terhadap kebutuhan ${domain.title.toLowerCase()}`
    : 'Analisis gap kapasitas vs kebutuhan aktual'

  // Stage 7: PetaEsok Readiness
  const isPetaEsokReady = isCalcReady && hasFinancial
  const stage7Status: StageStatus = isPetaEsokReady ? 'in_progress' : 'waiting_data'
  const stage7Label = isPetaEsokReady ? 'Siap dijelajahi' : 'Menunggu data'
  const stage7Subtitle = isPetaEsokReady
    ? `Visualisasi tahapan dan jalur alternatif ${domain.title.toLowerCase()}`
    : 'Visualisasi tahapan dan jalur alternatif'

  return [
    {
      id: 'goal',
      number: 1,
      title: 'Tujuan Masa Depan',
      subtitle: `${journey.goals.length} sasaran aktif (${activeGoal.name})`,
      status: 'completed',
      statusLabel: 'Selesai',
    },
    {
      id: 'reality',
      number: 2,
      title: 'Kondisi Saat Ini (Realita)',
      subtitle: hasFinancial
        ? `Arus kas terpetakan (${formatCompactIDR(netCapacity)}/bln)`
        : 'Arus kas & kesiapan tabungan',
      status: hasFinancial ? 'completed' : 'in_progress',
      statusLabel: hasFinancial ? 'Selesai' : 'Sedang dipetakan',
    },
    {
      id: 'planning_data',
      number: 3,
      title: 'Spesifikasi Sasaran',
      subtitle: stage3Subtitle,
      status: stage3Status,
      statusLabel: stage3Label,
    },
    {
      id: 'cost_funding',
      number: 4,
      title: 'Estimasi Biaya & Pendanaan',
      subtitle: stage4Subtitle,
      status: stage4Status,
      statusLabel: stage4Label,
    },
    {
      id: 'calculation',
      number: 5,
      title: 'Kalkulasi Deterministik',
      subtitle: stage5Subtitle,
      status: stage5Status,
      statusLabel: stage5Label,
    },
    {
      id: 'reality_check',
      number: 6,
      title: 'Reality Check',
      subtitle: stage6Subtitle,
      status: stage6Status,
      statusLabel: stage6Label,
    },
    {
      id: 'peta_esok',
      number: 7,
      title: 'PetaEsok (Peta Masa Depan)',
      subtitle: stage7Subtitle,
      status: stage7Status,
      statusLabel: stage7Label,
    },
    {
      id: 'next_steps',
      number: 8,
      title: 'Langkah Konkret',
      subtitle: `Tindakan prioritas dan snapshot perencanaan ${domain.title.toLowerCase()}`,
      status: 'waiting_data',
      statusLabel: 'Menunggu data',
    },
  ]
}

/**
 * Deterministic Next Action Rule Engine.
 * Driven by genuine domain readiness and scoped goal state.
 */
export function determineNextAction(
  journey: JourneyData,
  selectedGoalId?: string | null
): NextActionItem {
  if (journey.goals.length === 0) {
    return {
      id: 'action-add-goal',
      stage: 'Tujuan',
      title: 'Tentukan Tujuan Masa Depan Pertamamu',
      description:
        'Kamu belum memiliki tujuan bekal yang aktif. Pilih setidaknya satu tujuan untuk mulai memetakan kebutuhanmu.',
      ctaText: 'Pilih Tujuan Sekarang',
      ctaAction: 'add_goal',
      priority: 'high',
      badge: 'Langkah Awal',
    }
  }

  const activeGoal =
    (selectedGoalId ? journey.goals.find((g) => g.id === selectedGoalId) : null) ||
    journey.goals[0]!

  if (!journey.financialStatus.hasIncomes || !journey.financialStatus.hasExpenses) {
    const goalTitle = activeGoal ? `"${activeGoal.name}"` : 'rencana ini'
    return {
      id: 'action-financial-reality',
      stage: 'Kondisi Keuangan Saat Ini',
      title: 'Mulai Memetakan Realita Finansial Keluarga',
      description: `Untuk memastikan tujuan ${goalTitle} dapat dicapai secara realistis, kita perlu memetakan arus kas dasar (penghasilan dan pengeluaran bulanan) tanpa perlu tebak-tebakan.`,
      ctaText: 'Siapkan Data Keuangan',
      ctaAction: 'financial_reality',
      priority: 'high',
      badge: 'Langkah Bermakna Berikutnya',
    }
  }

  const domain = getGoalDomain(activeGoal.goalType)
  const targetEval = evaluateTargetReadiness(activeGoal.target, activeGoal.goalType)
  const p4Eval = evaluateP4Readiness(
    activeGoal.costItems || [],
    activeGoal.fundingSources || [],
    activeGoal.goalType
  )

  // Stage 3 Check: Target Specification
  if (!targetEval.isComplete) {
    const hasPartial = targetEval.completedFieldsCount > 0
    return {
      id: `action-target-specification-${activeGoal.id}`,
      stage: 'Spesifikasi Sasaran',
      title: hasPartial
        ? `Lanjutkan Spesifikasi "${activeGoal.name}"`
        : `Lengkapi Spesifikasi Sasaran Tujuan: ${activeGoal.name}`,
      description: hasPartial
        ? `Sasaran memiliki data parsial (${targetEval.completedFieldsCount}/${targetEval.totalRequiredFields}). Lengkapi ${targetEval.missingFields[0]?.toLowerCase() || 'data kunci'} agar siap dihitung pada tahap estimasi biaya.`
        : `Arus kas keluarga sudah terpetakan. Langkah selanjutnya adalah menentukan ${domain.targetStageLabel.toLowerCase()}, ${domain.programNameLabel.toLowerCase()}, lokasi operasional, dan target tahun pencapaian.`,
      ctaText: hasPartial ? 'Lanjutkan Spesifikasi' : 'Petakan Sasaran Sekarang',
      ctaAction: 'target_specification',
      goalId: activeGoal.id,
      priority: 'high',
      badge: hasPartial ? 'Langkah Selanjutnya' : 'Langkah Bermakna Berikutnya',
    }
  }

  // Stage 4 Check: Cost & Funding
  if (!p4Eval.isReadyForCalculation) {
    const costCount = activeGoal.costItems?.length || 0
    const hasPartialCost = costCount > 0
    return {
      id: `action-costing-${activeGoal.id}`,
      stage: 'Estimasi Biaya & Pendanaan',
      title: hasPartialCost
        ? `Lanjutkan Estimasi Biaya & Modal "${activeGoal.name}"`
        : `Petakan Kebutuhan Modal & Biaya "${activeGoal.name}"`,
      description: hasPartialCost
        ? `Sasaran memiliki ${costCount} komponen tercatat. Lengkapi kebutuhan modal dan potensi pendanaan agar siap dihitung.`
        : `Spesifikasi sasaran telah lengkap. Langkah berikutnya adalah mencatat estimasi modal awal, peralatan, izin usaha, atau modal kerja yang dibutuhkan untuk "${activeGoal.name}".`,
      ctaText: hasPartialCost ? 'Lanjutkan Estimasi Biaya' : 'Petakan Modal & Biaya',
      ctaAction: 'cost_funding',
      goalId: activeGoal.id,
      priority: 'high',
      badge: hasPartialCost ? 'Langkah Selanjutnya' : 'Langkah Bermakna Berikutnya',
    }
  }

  // All Ready: Run Calculation & Reality Check
  return {
    id: `action-run-calculation-${activeGoal.id}`,
    stage: 'Kalkulasi & Reality Check',
    title: `Jalankan Kalkulasi Proyeksi "${activeGoal.name}"`,
    description: `Seluruh struktur biaya/modal dan potensi pendanaan untuk "${activeGoal.name}" telah selesai dipetakan. Jalankan perhitungan deterministik dan Reality Check untuk menguji kesiapan kapasitas bekal keluarga.`,
    ctaText: 'Jalankan Perhitungan',
    ctaAction: 'run_calculation',
    goalId: activeGoal.id,
    priority: 'high',
    badge: 'Siap Dihitung',
  }
}

/**
 * Returns dynamic missing information based on actual scoped goal and financial state.
 */
export function getMissingInformation(
  journey: JourneyData,
  selectedGoalId?: string | null
): MissingInfoItem[] {
  const items: MissingInfoItem[] = []

  // 1. Financial Reality (Universal across all goals)
  if (!journey.financialStatus.hasIncomes && !journey.financialStatus.hasExpenses) {
    items.push({
      id: 'missing-finances',
      title: 'Kapasitas Arus Kas Bulanan Belum Dipetakan',
      description:
        'Penghasilan rutin, pengeluaran hidup esensial, dan komitmen kewajiban keluarga belum tercatat.',
      domain: 'financial',
      whyItMatters:
        'Tanpa data arus kas, sistem tidak dapat menghitung sisa kapasitas tabungan bulanan yang bisa dialokasikan.',
    })
  } else if (!journey.financialStatus.hasIncomes) {
    items.push({
      id: 'missing-finances-income',
      title: 'Pemasukan Rutin Belum Dicatat',
      description:
        'Pengeluaran telah tercatat, namun pemasukan rutin belum dimasukkan untuk mengetahui kapasitas tabungan.',
      domain: 'financial',
      whyItMatters:
        'Pemasukan diperlukan untuk menentukan apakah arus kas keluarga menghasilkan surplus tabungan.',
    })
  } else if (!journey.financialStatus.hasExpenses) {
    items.push({
      id: 'missing-finances-expense',
      title: 'Pengeluaran Hidup Belum Dicatat',
      description:
        'Pemasukan telah tercatat, namun pengeluaran hidup esensial belum dimasukkan untuk mengetahui kapasitas tabungan bersih.',
      domain: 'financial',
      whyItMatters:
        'Pengeluaran mengurangi pemasukan bulanan untuk menghasilkan angka kapasitas riil yang aman dialokasikan.',
    })
  } else if (!journey.financialStatus.hasSavings) {
    items.push({
      id: 'missing-savings',
      title: 'Saldo Bekal / Tabungan Awal Belum Dicatat',
      description:
        'Belum ada dana awal atau simpanan yang dialokasikan khusus untuk tujuan ini.',
      domain: 'funding',
      whyItMatters:
        'Saldo awal mengurangi jumlah akumulasi bulanan yang harus ditabung setiap periode.',
    })
  }

  // 2. Scoped Goal Unknowns
  const activeGoal =
    (selectedGoalId ? journey.goals.find((g) => g.id === selectedGoalId) : null) ||
    journey.goals[0]

  if (activeGoal) {
    const domain = getGoalDomain(activeGoal.goalType)
    const targetEval = evaluateTargetReadiness(activeGoal.target, activeGoal.goalType)

    if (!targetEval.isComplete) {
      if (targetEval.completedFieldsCount > 0) {
        items.push({
          id: `missing-target-${activeGoal.id}`,
          title: `Spesifikasi "${activeGoal.name}" Belum Lengkap`,
          description: `Data yang masih dibutuhkan: ${targetEval.missingFields.join(', ')}.`,
          domain: 'target_detail',
          whyItMatters: `Parameter ini diperlukan agar estimasi kebutuhan biaya/modal ${domain.title.toLowerCase()} dapat ditentukan secara presisi.`,
        })
      } else {
        items.push({
          id: activeGoal.goalType === 'education' ? 'missing-edu-target' : `missing-target-${activeGoal.id}`,
          title: `${domain.targetStageLabel} & ${domain.programNameLabel} Belum Ditentukan`,
          description: `Target ${domain.targetStageLabel.toLowerCase()}, ${domain.programNameLabel.toLowerCase()}, lokasi, dan tahun belum dirinci untuk sasaran ini.`,
          domain: 'target_detail',
          whyItMatters: `Biaya dan modal sangat bergantung pada skala dan jangka waktu sasaran ${domain.title.toLowerCase()} yang dipilih.`,
        })
      }
    }

    const p4Eval = evaluateP4Readiness(
      activeGoal.costItems || [],
      activeGoal.fundingSources || [],
      activeGoal.goalType
    )
    if (!p4Eval.isReadyForCalculation) {
      items.push({
        id: activeGoal.goalType === 'education' ? 'missing-edu-cost' : `missing-cost-${activeGoal.id}`,
        title: `Komponen Biaya / Modal ${domain.title} Belum Dipetakan`,
        description: `Belum ada komponen modal awal, peralatan, izin usaha, atau operasional yang dicatat untuk "${activeGoal.name}".`,
        domain: 'planning',
        whyItMatters: `Dibutuhkan angka dasar biaya agar kalkulasi dapat memproyeksikan kebutuhan bekal masa depan.`,
      })
    } else if (!activeGoal.fundingSources || activeGoal.fundingSources.length === 0) {
      items.push({
        id: activeGoal.goalType === 'education' ? 'missing-edu-funding' : `missing-funding-${activeGoal.id}`,
        title: `Belum Ada Sumber Pendanaan yang Dicatat`,
        description: `Belum ada potensi modal mandiri, tabungan dialokasikan, atau sponsor yang didaftarkan untuk "${activeGoal.name}".`,
        domain: 'funding',
        whyItMatters: `Mencatat sumber pendanaan membantu memetakan berapa porsi modal yang masih harus dipersiapkan dari kapasitas tabungan bulanan.`,
      })
    }
  }

  // 3. Goal-type specific legacy checks for backwards compatibility
  const goalTypes = new Set(journey.goals.map((g) => g.goalType))

  if (goalTypes.has('career')) {
    items.push({
      id: 'missing-career-milestones',
      title: 'Horizon Waktu & Kebutuhan Keterampilan Belum Dirinci',
      description:
        'Peran sasaran, kebutuhan pelatihan/sertifikasi, dan estimasi waktu persiapan belum dipetakan.',
      domain: 'milestones',
      whyItMatters:
        'Menentukan runway (cadangan dana bertahan hidup) yang dibutuhkan selama masa transisi karier.',
    })
  }

  if (goalTypes.has('housing')) {
    items.push({
      id: 'missing-housing-params',
      title: 'Estimasi Nilai Properti & Skema Pembiayaan Belum Dipilih',
      description:
        'Perkiraan harga hunian sasaran, kesiapan uang muka (DP), dan preferensi skema cicilan belum dicatat.',
      domain: 'target_detail',
      whyItMatters:
        'Kalkulator membutuhkan parameter ini untuk menguji rasio cicilan terhadap kapasitas pendapatan bulanan.',
    })
  }

  if (goalTypes.has('financial') || goalTypes.has('emergency')) {
    items.push({
      id: 'missing-runway-target',
      title: 'Target Jumlah Bulan Cadangan Belum Ditetapkan',
      description:
        'Belum ditentukan berapa bulan biaya hidup yang ingin diamankan (standar aman 3, 6, atau 12 bulan).',
      domain: 'target_detail',
      whyItMatters:
        'Menentukan target nominal total dana darurat berdasarkan pengeluaran esensial keluarga.',
    })
  }

  return items
}
